import ConfirmDialog from 'components/common/ConfirmDialog';
import DeleteDialog from 'components/common/DeleteDialog';
import { node } from 'prop-types';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { I18n } from 'utils/i18n';
import useOpenClose from 'utils/useOpenClose';

export const ConfirmDialogContext = React.createContext({});

const GlobalConfirmDialogProvider = ({ children }) => {
  const confirmRef = useRef();
  const rippleRef = useRef();
  const clearButtonsTimer = useRef(null);
  const [isOpen, openDialog, closeDialog, args] = useOpenClose();
  const [buttons, setButtons] = useState([]);
  const onClose = useRef(null);
  const close = useCallback(() => {
    clearTimeout(clearButtonsTimer.current);
    clearButtonsTimer.current = setTimeout(() => {
      setButtons([]);
      onClose.current = null;
      clearButtonsTimer.current = null;
    }, 500);
    if (typeof onClose.current === 'function') {
      onClose.current();
    }
    closeDialog();
  }, [closeDialog]);
  const open = useCallback(
    newArgs => {
      if (isOpen) {
        return;
      }
      clearTimeout(clearButtonsTimer.current);
      clearButtonsTimer.current = null;
      onClose.current = newArgs.onClose;
      const customButtons =
        Array.isArray(newArgs.buttons) && newArgs.buttons.length > 0 ? newArgs.buttons : null;
      if (customButtons) {
        const submitIdx = customButtons.findIndex(b => b.type === 'submit');
        const focusIdx = submitIdx >= 0 ? submitIdx : customButtons.length - 1;
        setButtons(
          customButtons.map((btn, idx) => ({
            variant: 'outlined',
            color: 'secondary',
            type: 'button',
            ...btn,
            ...(idx === focusIdx
              ? { ref: confirmRef, touchRippleRef: rippleRef, type: btn.type || 'submit' }
              : {}),
          })),
        );
      } else {
        setButtons([
          {
            text: newArgs.cancelText || I18n.t('ConfirmDialog.Cancel'),
            onClick: close,
            ...(newArgs.cancelBtn || {}),
          },
          {
            text: newArgs.confirmText || I18n.t('ConfirmDialog.Confirm'),
            onClick: newArgs.onConfirm || close,
            variant: 'contained',
            color: 'primary',
            ...(newArgs.confirmBtn || {}),
            type: 'submit',
            ref: confirmRef,
            touchRippleRef: rippleRef,
          },
        ]);
      }
      openDialog(newArgs);
      setTimeout(() => {
        confirmRef.current?.focus();
        rippleRef.current?.stop();
        rippleRef.current?.pulsate();
      }, 100);
    },
    [isOpen, openDialog, close, confirmRef],
  );
  const contextValue = useMemo(() => ({ open, close, isOpen }), [open, close, isOpen]);
  return (
    <ConfirmDialogContext.Provider value={contextValue}>
      {children}
      {args?.isDelete ? (
        <DeleteDialog
          message={args?.message}
          typeBack={args?.typeBack}
          onClose={close}
          isOpen={isOpen}
          onConfirm={args?.onConfirm}
        >
          {args?.children}
        </DeleteDialog>
      ) : (
        <ConfirmDialog message={args?.message} buttons={buttons} onClose={close} isOpen={isOpen}>
          {args?.children}
        </ConfirmDialog>
      )}
    </ConfirmDialogContext.Provider>
  );
};

GlobalConfirmDialogProvider.propTypes = {
  children: node.isRequired,
};

export default GlobalConfirmDialogProvider;
